import { create } from 'zustand';

/**
 * Lightweight UI signal bus. `homeScrollTop` is bumped when the user re-taps
 * the active Découvre tab — the Home screen subscribes and scrolls its deck to
 * the top (most recent recommendation).
 */
interface UiSignals {
  homeScrollTop: number;
  bumpHomeScrollTop: () => void;
  /** After sharing a reco, the circle Home should land on (first one shared with). */
  landOnCircleId: number | null;
  setLandOnCircle: (id: number | null) => void;
  /**
   * Deep-link to one song card: tapping a title/artist in "My Recos" asks Home
   * to scroll its deck to that reco (paging forward until it shows up).
   */
  landOnRecoId: number | null;
  /**
   * reco-comment-replies v2 — open the conversation on this reco once its card
   * is mounted. A signal rather than a route: the app already lands on a card,
   * and a route cost the user their circle and their position on close.
   */
  openThreadForRecoId: number | null;
  setLandOnReco: (id: number | null) => void;
  setOpenThreadForReco: (id: number | null) => void;
  /**
   * feed-position-and-pause — the card each circle was left on, by RECO ID
   * (pixels would drift as soon as a reco lands on top). Module memory: it
   * survives Home unmounting for the session, and is never persisted — after a
   * relaunch every circle starts from the top, on what is new.
   */
  deckPositions: Record<number, number>;
  rememberDeckPosition: (circleId: number, recoId: number) => void;
  deckPositionFor: (circleId: number) => number | undefined;
}

export const useUiSignals = create<UiSignals>((set, get) => ({
  homeScrollTop: 0,
  bumpHomeScrollTop: () => set((s) => ({ homeScrollTop: s.homeScrollTop + 1 })),
  landOnCircleId: null,
  setLandOnCircle: (id) => set({ landOnCircleId: id }),
  landOnRecoId: null,
  openThreadForRecoId: null,
  setLandOnReco: (id) => set({ landOnRecoId: id }),
  setOpenThreadForReco: (id) => set({ openThreadForRecoId: id }),
  deckPositions: {},
  rememberDeckPosition: (circleId, recoId) => {
    // Called on every scroll event: skip the write when nothing moved.
    if (get().deckPositions[circleId] === recoId) return;
    set((s) => ({ deckPositions: { ...s.deckPositions, [circleId]: recoId } }));
  },
  deckPositionFor: (circleId) => get().deckPositions[circleId],
}));
